'use client';

import React, { useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Chart } from './Chart';
import { ChartHamburgerMenu } from './combined/ChartHamburgerMenu';
import { ChartSkeleton } from './ChartSkeleton';
import { theme } from '../../config/theme';
import type { ChartData, ChartLayout, ChartConfig } from '../../types/chart';

const LayeredDonutChartContainer = styled.div<{ $isFullscreen: boolean }>`
  overflow: visible;
  transition: all 0.3s ease;
  background: transparent;
  
  ${({ $isFullscreen }) =>
    $isFullscreen &&
    `
    height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;
    padding: 40px;
    background: ${theme.colors.white};
  `}
`;

const ChartHeader = styled.div`
  padding: 16px 20px 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const ChartTitle = styled.h3`
  display: block;
  margin: 0;
  font-size: 0.75rem;
  font-weight: ${theme.fontWeights.semibold};
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ChartDescription = styled.p`
  display: block;
  margin: 4px 0 0 0;
  font-size: 0.875rem;
  color: #6b7280;
`;

const ChartContent = styled.div`
  padding: ${theme.spacing.lg};
  min-height: 400px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
`;

const ChartWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 400px;
`;

const HamburgerMenuWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
`;

const LabelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 220px;
  padding-right: 32px;
  justify-content: center;
`;

const LabelItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  padding: 4px 0;
`;

const LabelText = styled.span<{ $color: string }>`
  font-size: 14px;
  font-weight: ${theme.fontWeights.medium};
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

const ConnectingLine = styled.div<{ $color: string }>`
  position: absolute;
  left: calc(100% + 8px);
  top: 50%;
  width: 80px;
  height: 1px;
  background: linear-gradient(to right, ${({ $color }) => $color} 0%, ${({ $color }) => $color} 30%, #e5e7eb 30%);
  transform: translateY(-50%);
  z-index: 1;
`;

export interface LayeredDonutElement {
  element: string;
  totalChecks: number;
  present: number;
  successRate: number; // 0-100
}

export interface LayeredDonutChartProps {
  data: LayeredDonutElement[];
  height?: number;
  loading?: boolean;
  className?: string;
}

// Color palette for elements - each element gets a unique color
const ELEMENT_COLORS = [
  '#EC221F', // red
  '#BF6A02', // orange
  '#F59E0B', // yellow/amber
  '#14AE5C', // green
  '#06B6D4', // cyan/light blue
  '#3B82F6', // blue
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#10B981', // emerald
  '#F97316', // orange-500
];

// Get unique color for each element by index
const getElementColor = (index: number): string => {
  return ELEMENT_COLORS[index % ELEMENT_COLORS?.length];
};

export const LayeredDonutChart: React.FC<LayeredDonutChartProps> = ({
  data,
  height = 400,
  loading = false,
  className
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [zoomEnabled, setZoomEnabled] = useState(false);

  // Sort data by success rate (descending) for better visualization
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => b.successRate - a.successRate);
  }, [data]);

  // Generate chart data with layered donut effect
  // Each element is a separate concentric ring, with arc length representing success rate
  // Using multiple pie charts with different hole sizes to create concentric rings
  const chartData: ChartData[] = useMemo(() => {
    if (!sortedData || sortedData?.length === 0) {
      return [];
    }

    const traces: ChartData[] = [];
    const numElements = sortedData?.length;
    
    // Calculate hole sizes for concentric rings
    // Each ring needs to be progressively smaller to create the layered effect
    // The hole size determines how much of the center is empty
    const minHole = 0.2; // Innermost ring (largest radius) - smallest hole
    const maxHole = 0.8; // Outermost ring (smallest radius) - largest hole
    const holeStep = numElements > 1 ? (maxHole - minHole) / (numElements - 1) : 0;

    sortedData.forEach((element, index) => {
      // Reverse the index so outer rings (visually larger) have smaller holes
      // index 0 (innermost visually) = largest hole (0.8)
      // index N (outermost visually) = smallest hole (0.2)
      const holeSize = maxHole - (index * holeStep);
      
      const successRate = element.successRate;
      const color = getElementColor(index); // Use unique color per element
      const failureRate = 100 - successRate;

      // Create pie chart trace for this ring
      // The arc length represents the success rate percentage
      traces.push({
        type: 'pie',
        labels: [element.element, ''],
        values: [successRate, failureRate],
        hole: holeSize,
        marker: {
          colors: [color, 'rgba(240, 240, 240, 0.2)'],
          line: {
            color: theme.colors.white,
            width: 3,
          },
        },
        textinfo: 'none',
        hoverinfo: 'label+text+percent',
        hovertemplate: `<b>${element.element}</b><br>Total Checks: ${element.totalChecks}<br>Present: ${element.present}<br>Absent: ${element.totalChecks - element.present}<br>Success Rate: ${successRate.toFixed(1)}%<extra></extra>`,
        showlegend: false,
        rotation: -90, // Start from bottom (6 o'clock position)
        direction: 'clockwise',
        sort: false,
        domain: {
          x: [0, 1],
          y: [0, 1],
        },
      } as ChartData[][0]);
    });

    return traces;
  }, [sortedData]);


  const layout: ChartLayout = useMemo(() => ({
    height,
    hovermode: 'closest',
    margin: {
      l: 20,
      r: 20,
      t: 20,
      b: 20,
    },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      color: theme.colors.gray[700],
      family: 'Arial, sans-serif',
    },
    showlegend: false,
  }), [height]);

  // Show skeleton if loading
  if (loading) {
    return <ChartSkeleton type="pie" height={`${height}px`} showLegend />;
  }

  const config: ChartConfig = {
    displayModeBar: false,
    displaylogo: false,
    staticPlot: !zoomEnabled,
    responsive: true,
    scrollZoom: zoomEnabled,
    doubleClick: zoomEnabled ? 'reset' : false,
    modeBarButtonsToRemove: zoomEnabled 
      ? ['lasso2d', 'select2d', 'autoScale2d', 'resetScale2d']
      : ['lasso2d', 'select2d', 'pan2d', 'zoom2d', 'autoScale2d', 'resetScale2d'],
  };

  // Create table data for hamburger menu
  const tableData: Array<Record<string, string | number>> = sortedData.map((element) => ({
    element: element.element,
    totalChecks: element.totalChecks,
    present: element.present,
    successRate: `${element.successRate.toFixed(1)}%`
  }));

  return (
    <LayeredDonutChartContainer ref={chartRef} $isFullscreen={false} className={className}>
      <ChartContent>
        {sortedData?.length > 0 ? (
          <>
            <LabelsContainer>
              {sortedData.map((element, index) => {
                const color = getElementColor(index);
                return (
                  <LabelItem key={`${element.element}-${index}`} $color={color}>
                    <LabelText $color={color}>
                      {element.element} ({element.successRate.toFixed(0)}%)
                    </LabelText>
                    <ConnectingLine 
                      $color={color}
                    />
                  </LabelItem>
                );
              })}
            </LabelsContainer>
            <ChartWrapper>
              <HamburgerMenuWrapper>
                <ChartHamburgerMenu 
                  chartTitle="Functional Element Checks"
                  tableData={tableData}
                  chartContainerRef={chartRef}
                  onZoomToggle={setZoomEnabled}
                  initialZoomEnabled={zoomEnabled}
                />
              </HamburgerMenuWrapper>
              <Chart
                data={chartData}
                layout={layout}
                config={config}
                revision={zoomEnabled ? 1 : 0}
              />
            </ChartWrapper>
          </>
        ) : (
          <div style={{ textAlign: 'center', color: theme.colors.gray[500], width: '100%' }}>
            No data available
          </div>
        )}
      </ChartContent>
    </LayeredDonutChartContainer>
  );
};

